import React, { createContext, useContext, useEffect, useReducer } from "react";
import { apiFetch } from "../lib/api";
import {
  AuthContextType,
  AuthState,
  LoginCredentials,
  RegisterCredentials,
  User,
} from "../types/auth";

// Note: Hardcoded users removed - now using real API authentication

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true for token verification
  error: null,
};

// Action types
type AuthAction =
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; payload: User }
  | { type: "LOGIN_FAILURE"; payload: string }
  | { type: "REGISTER_START" }
  | { type: "REGISTER_SUCCESS"; payload: User }
  | { type: "REGISTER_FAILURE"; payload: string }
  | { type: "LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "LOAD_USER"; payload: User }
  | { type: "VERIFICATION_COMPLETE" };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN_START":
    case "REGISTER_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
    case "REGISTER_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "LOGIN_FAILURE":
    case "REGISTER_FAILURE":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "LOAD_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "VERIFICATION_COMPLETE":
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from token on mount
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          const response = await apiFetch("/auth/verify");

          const result = await response.json();
          if (result.success && result.user) {
            dispatch({ type: "LOAD_USER", payload: result.user });
          } else {
            localStorage.removeItem("authToken");
            dispatch({ type: "VERIFICATION_COMPLETE" });
          }
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("authToken");
          dispatch({ type: "VERIFICATION_COMPLETE" });
        }
      } else {
        dispatch({ type: "VERIFICATION_COMPLETE" });
      }
    };

    verifyToken();
  }, []);

  // Save user to sessionStorage when authenticated
  useEffect(() => {
    if (state.user) {
      sessionStorage.setItem("user", JSON.stringify(state.user));
    } else {
      sessionStorage.removeItem("user");
    }
  }, [state.user]);

  const requestOTP = async (
    email: string,
    purpose: "register" | "login"
  ): Promise<boolean> => {
    // Do not toggle global loading here; it causes the LoginPage to unmount
    // which clears local OTP state. Just clear any prior error.
    dispatch({ type: "CLEAR_ERROR" });

    try {
      const lower = email.trim().toLowerCase();
      if (purpose === "register" && !lower.endsWith("@iiitd.ac.in")) {
        dispatch({
          type: "REGISTER_FAILURE",
          payload:
            "Only IIITD (@iiitd.ac.in) emails can self-register. Please request access via the form.",
        });
        return false;
      }
      const endpoint =
        purpose === "register"
          ? "/auth/otp/request-register"
          : "/auth/otp/request-login";

      const response = await apiFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.success) {
        return true;
      } else {
        dispatch({
          type: "LOGIN_FAILURE",
          payload: result.error || "Failed to send OTP",
        });
        return false;
      }
    } catch (error) {
      console.error("OTP request error:", error);
      dispatch({
        type: "LOGIN_FAILURE",
        payload: "Network error. Please check your connection and try again.",
      });
      return false;
    }
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: "LOGIN_START" });

    // Basic validation
    if (!credentials.email || !credentials.otp) {
      dispatch({
        type: "LOGIN_FAILURE",
        payload: "Email and OTP are required",
      });
      return false;
    }

    try {
      const response = await apiFetch("/auth/login-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: credentials.email, otp: credentials.otp }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.token) {
          localStorage.setItem("authToken", result.token);
        }
        dispatch({ type: "LOGIN_SUCCESS", payload: result.user });
        return true;
      } else {
        let errorMessage = result.error || "Login failed";
        if (response.status === 401) {
          errorMessage = "Invalid or expired OTP. Please try again.";
        } else if (response.status === 400) {
          errorMessage = "Please fill in all required fields.";
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }
        dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "Network error. Please check your connection and try again.";
      if (error instanceof TypeError && error.message.includes("fetch")) {
        errorMessage = "Cannot connect to server. Please make sure the backend is running.";
      }
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      return false;
    }
  };

  const register = async (
    credentials: RegisterCredentials
  ): Promise<boolean> => {
    dispatch({ type: "REGISTER_START" });

    // Basic validation (OTP must have been sent and entered)
    if (!credentials.email || !credentials.name || !credentials.role || !credentials.otp) {
      dispatch({
        type: "REGISTER_FAILURE",
        payload: "All fields and OTP are required",
      });
      return false;
    }

    const lower = credentials.email.trim().toLowerCase();
    if (!lower.endsWith("@iiitd.ac.in")) {
      dispatch({
        type: "REGISTER_FAILURE",
        payload:
          "Only IIITD (@iiitd.ac.in) emails can self-register. Please request access via the Google Form.",
      });
      return false;
    }

    try {
      const response = await apiFetch("/auth/register-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: credentials.email,
          name: credentials.name,
          role: credentials.role,
          otp: credentials.otp,
          vehicleInfo: credentials.role === "driver" ? credentials.vehicleInfo : undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.token) {
          localStorage.setItem("authToken", result.token);
        }
        dispatch({ type: "REGISTER_SUCCESS", payload: result.user });
        return true;
      } else {
        let errorMessage = result.error || "Registration failed";
        if (response.status === 400) {
          if (result.error?.includes("already exists")) {
            errorMessage = "An account with this email already exists. Please use a different email or log in.";
          } else if (result.error?.includes("OTP") || result.error?.includes("otp")) {
            errorMessage = result.error;
          } else {
            errorMessage = "Please check your information and try again.";
          }
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }
        dispatch({ type: "REGISTER_FAILURE", payload: errorMessage });
        return false;
      }
    } catch (error) {
      console.error("Registration error:", error);
      let errorMessage = "Network error. Please check your connection and try again.";
      if (error instanceof TypeError && error.message.includes("fetch")) {
        errorMessage = "Cannot connect to server. Please make sure the backend is running.";
      }
      dispatch({ type: "REGISTER_FAILURE", payload: errorMessage });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    dispatch({ type: "LOGOUT" });
  };

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    requestOTP,
    mockGoogleLogin: async () => {
      // Mock function for backward compatibility - just return false
      dispatch({
        type: "LOGIN_FAILURE",
        payload: "Google login not available",
      });
      return false;
    },
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
